import { useState, useMemo, useCallback, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import Papa from 'papaparse';
import { api } from '../api/client';
import type {
  Project, BusinessUnit, SystemRow,
  LikertLevel, BulkCreateResult,
} from '../api/types';

// =============================================================================
// Slice 6b: Bulk asset creation page.
//
// User flow:
//   1. Paste rows from Excel (or click "Add row" repeatedly).
//   2. Each row is validated as it changes — invalid cells get a red ring.
//   3. "Create N assets" button is enabled only when every row is valid.
//   4. On submit, all rows are sent in one POST to bulk-create-and-enroll.
//
// Columns (kept narrow on purpose — paste-from-Excel works best with a
// small column set; deep edit is one-by-one in the existing AssetForm):
//
//   Name | Type | System | Criticality | Exposure | Vendor supplied
//
// Type is matched by name (case-insensitive contains) against the
// catalog, fallback to first match. System is matched the same way
// against the systems for this project's BU. Owner / external_ref /
// migration-feasibility fields default to null and are filled in via
// the AssetForm later.
// =============================================================================

interface RowDraft {
  rowKey: number;  // stable key for React; not stored
  name: string;
  type: string;
  system: string;
  criticality: string;
  exposure: string;
  vendor: string;  // Yes / No / blank (gates → No default)
}

interface AssetType {
  id: string;
  code: string;
  name: string;
  family: string | null;
}
interface AssetSource {
  id: string;
  code: string;
  name: string;
}

const EMPTY_ROW = (k: number): RowDraft => ({
  rowKey: k,
  name: '',
  type: '',
  system: '',
  criticality: 'Moderate',
  exposure: 'Internal_only',
  vendor: 'No',
});

const LIKERT: LikertLevel[] = ['Very_low', 'Low', 'Moderate', 'High', 'Very_high'];
const EXPOSURES = ['None', 'Internal_only', 'Partner', 'Internet_facing', 'Air_gapped', 'Unknown'] as const;

interface ValidationErrors {
  name?: string;
  type?: string;
  system?: string;
  criticality?: string;
  exposure?: string;
  vendor?: string;
}

export function BulkCreateAssets() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  const [rows, setRows] = useState<RowDraft[]>(() => Array.from({ length: 5 }, (_, i) => EMPTY_ROW(i)));
  const [keyCounter, setKeyCounter] = useState(5);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Data needed to map type/system names → IDs and validate
  const projectQ = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.get<Project>(`/projects/${projectId}`),
    enabled: !!projectId,
  });
  const busQ = useQuery({
    queryKey: ['bus', projectQ.data?.scope_company_id],
    queryFn: () =>
      api.get<BusinessUnit[]>(`/business-units?company_id=${projectQ.data!.scope_company_id}`),
    enabled: !!projectQ.data?.scope_company_id,
  });
  const typesQ = useQuery({
    queryKey: ['asset-types'],
    queryFn: () => api.get<AssetType[]>('/asset-types'),
  });
  const sourcesQ = useQuery({
    queryKey: ['asset-sources'],
    queryFn: () => api.get<AssetSource[]>('/asset-sources'),
  });

  // Systems for the project's BU (or first BU under the company if
  // project is Company-level — in which case any system in any of those
  // BUs is in scope, but for simplicity we offer the union).
  const systemsQ = useQuery({
    queryKey: ['project-systems', projectId, busQ.data?.map((b) => b.id)],
    queryFn: async () => {
      if (!busQ.data) return [];
      const all: SystemRow[] = [];
      // If project is BU-scoped, only that BU. Otherwise all BUs of the
      // company.
      const targetBuIds = projectQ.data?.scope_business_unit_id
        ? [projectQ.data.scope_business_unit_id]
        : busQ.data.map((b) => b.id);
      for (const buId of targetBuIds) {
        const list = await api.get<SystemRow[]>(`/systems?business_unit_id=${buId}`);
        all.push(...list);
      }
      return all;
    },
    enabled: !!busQ.data,
  });

  // Default source: prefer Manual_entry; otherwise first.
  const defaultSourceId = useMemo(() => {
    const list = sourcesQ.data ?? [];
    const manual = list.find((s) => s.code === 'Manual_entry');
    return manual?.id ?? list[0]?.id ?? null;
  }, [sourcesQ.data]);

  // Match by name → id helpers
  const typesByName = useMemo(() => {
    const map = new Map<string, AssetType>();
    for (const t of typesQ.data ?? []) {
      map.set(t.name.toLowerCase(), t);
      // Also allow matching by short code suffix (e.g. "database" matches data.database)
      const codeTail = t.code.split('.').pop() ?? t.code;
      map.set(codeTail.toLowerCase(), t);
    }
    return map;
  }, [typesQ.data]);

  const systemsByName = useMemo(() => {
    const map = new Map<string, SystemRow>();
    for (const s of systemsQ.data ?? []) {
      map.set(s.name.toLowerCase(), s);
      map.set(s.short_code.toLowerCase(), s);
    }
    return map;
  }, [systemsQ.data]);

  // Per-row validation
  const validateRow = useCallback((row: RowDraft): ValidationErrors => {
    const errs: ValidationErrors = {};
    if (!row.name.trim()) errs.name = 'Name required';
    else if (row.name.length > 255) errs.name = 'Name too long';

    if (!row.type.trim()) {
      errs.type = 'Type required';
    } else if (!typesByName.has(row.type.toLowerCase().trim())) {
      errs.type = `Unknown type. Try: ${(typesQ.data ?? []).slice(0, 3).map((t) => t.name).join(', ')}`;
    }

    if (!row.system.trim()) {
      errs.system = 'System required';
    } else if (!systemsByName.has(row.system.toLowerCase().trim())) {
      errs.system = `Unknown system. Available: ${(systemsQ.data ?? []).map((s) => s.name).join(', ') || '(none)'}`;
    }

    if (!LIKERT.includes(row.criticality as LikertLevel)) errs.criticality = 'Pick a level';
    if (!(EXPOSURES as readonly string[]).includes(row.exposure)) errs.exposure = 'Pick an exposure';
    if (!['Yes', 'No'].includes(row.vendor)) errs.vendor = 'Yes or No';

    return errs;
  }, [typesByName, systemsByName, typesQ.data, systemsQ.data]);

  // Compute validation per-row, plus whether the whole batch is valid
  // and non-empty (we never submit completely-blank rows).
  const validation = useMemo(() => {
    return rows.map((r) => {
      const isBlank = !r.name && !r.type && !r.system;
      if (isBlank) return { isBlank: true, errors: {} as ValidationErrors };
      return { isBlank: false, errors: validateRow(r) };
    });
  }, [rows, validateRow]);

  const submittableRows = rows.filter((_, i) => !validation[i]!.isBlank);
  const allValid = submittableRows.length > 0 &&
    submittableRows.every((_, i) => {
      const idx = rows.findIndex((_, j) => j === i);
      return Object.keys(validation[idx]?.errors ?? {}).length === 0;
    });

  // Update single cell
  const updateCell = (rowIndex: number, key: keyof RowDraft, value: string) => {
    setRows((prev) => prev.map((r, i) => i === rowIndex ? { ...r, [key]: value } : r));
  };

  const addRow = () => {
    setRows((prev) => [...prev, EMPTY_ROW(keyCounter)]);
    setKeyCounter((k) => k + 1);
  };

  const removeRow = (rowIndex: number) => {
    setRows((prev) => prev.filter((_, i) => i !== rowIndex));
  };

  const clearAll = () => {
    setRows([EMPTY_ROW(keyCounter)]);
    setKeyCounter((k) => k + 1);
  };

  // ---------- Paste handler — Excel/Sheets row paste support ----------
  // When the user pastes into the table, intercept the paste event and
  // parse it as TSV/CSV. Each pasted row replaces a draft row (or
  // appends if there aren't enough rows). The paste target's column is
  // the starting column; subsequent values fill columns to the right.
  const onPaste = useCallback((e: React.ClipboardEvent<HTMLElement>, startRowIndex: number, startColIndex: number) => {
    const text = e.clipboardData.getData('text');
    if (!text || (!text.includes('\t') && !text.includes('\n'))) return;
    e.preventDefault();

    const parsed = Papa.parse<string[]>(text.trim(), {
      delimiter: text.includes('\t') ? '\t' : ',',
      skipEmptyLines: true,
    });
    const cols: Array<Exclude<keyof RowDraft, 'rowKey'>> = ['name', 'type', 'system', 'criticality', 'exposure', 'vendor'];
    const startKey = cols[startColIndex];
    if (!startKey) return;

    setRows((prev) => {
      const next = [...prev];
      for (let r = 0; r < parsed.data.length; r++) {
        const targetIdx = startRowIndex + r;
        if (targetIdx >= next.length) {
          next.push(EMPTY_ROW(keyCounter + r));
        }
        const cells = parsed.data[r] ?? [];
        const updated = { ...next[targetIdx]! };
        for (let c = 0; c < cells.length; c++) {
          const colKey = cols[startColIndex + c];
          if (!colKey) break;
          updated[colKey] = (cells[c] ?? '').trim();
        }
        next[targetIdx] = updated;
      }
      return next;
    });
    setKeyCounter((k) => k + parsed.data.length);
  }, [keyCounter]);

  // ---------- Submit ----------
  const submit = useMutation({
    mutationFn: async () => {
      if (!projectQ.data || !defaultSourceId) {
        throw new Error('Project or asset-source catalog not yet loaded');
      }
      const project = projectQ.data;
      const assets = submittableRows.map((row) => {
        const type = typesByName.get(row.type.toLowerCase().trim())!;
        const system = systemsByName.get(row.system.toLowerCase().trim())!;
        const typeAttrs: Record<string, unknown> = {
          vendor_supplied_asset: row.vendor,
        };
        if (row.vendor === 'Yes') {
          // Sensible defaults so Supply Chain has values to score; the
          // user can refine in the AssetForm. Without these defaults,
          // the engine would resolve them via the cascade and likely
          // produce zeros.
          typeAttrs['vendor_readiness'] = 'Moderate';
          typeAttrs['active_engagement'] = 'Moderate';
          typeAttrs['alternative_sourcing_difficulty'] = 'Moderate';
          typeAttrs['procurement_timelines_cycles'] = 'Moderate'; // Likert value (was 'Medium', not a valid level)
          typeAttrs['source_country_risk'] = 'Low';
        }
        return {
          company_id: system.business_unit_id
            ? // We have BU id on the system row; resolve company via BU lookup.
              (busQ.data?.find((b) => b.id === system.business_unit_id)?.company_id
                ?? project.scope_company_id!)
            : project.scope_company_id!,
          business_unit_id: system.business_unit_id,
          system_id: system.id,
          asset_type_id: type.id,
          asset_source_id: defaultSourceId,
          name: row.name.trim(),
          business_criticality: row.criticality as LikertLevel,
          expected_shelf_life: 5,
          time_for_migration: 3,
          crypto_agility: 'L2_Defined',
          upgradability: 'Moderate',
          legacy_system: false,
          hardware_resource_constraints: 'Moderate',
          certification_requirement: false,
          source_code_availability: true,
          external_exposure: row.exposure,
          lifecycle_status: 'Active',
          type_attributes: typeAttrs,
        };
      });
      return api.post<BulkCreateResult>(
        `/projects/${projectId}/assets/bulk-create`,
        { assets },
      );
    },
    onSuccess: () => {
      navigate(`/projects/${projectId}?tab=assets`);
    },
    onError: (e: Error) => {
      setSubmitError(e.message);
    },
  });

  // Pre-fill blank submission error when validation passes again
  useEffect(() => { if (allValid) setSubmitError(null); }, [allValid]);

  if (projectQ.isLoading || typesQ.isLoading || sourcesQ.isLoading) {
    return <div className="p-8 text-ink-500 text-sm">Loading…</div>;
  }
  if (!projectQ.data) {
    return <div className="p-8 text-accent text-sm">Project not found.</div>;
  }

  return (
    <div className="p-6 max-w-[1280px] mx-auto">
      <div className="mb-4">
        <Link to={`/projects/${projectId}`} className="text-xs text-ink-500 hover:text-ink-900">
          ← Back to project
        </Link>
      </div>
      <h1 className="font-display text-2xl text-ink-900 mb-2">Bulk add assets</h1>
      <p className="text-sm text-ink-500 mb-6 max-w-2xl">
        Add multiple assets at once. Paste from Excel or Google Sheets — the
        clipboard parser will fill rows starting from the cell you click in.
        Type and System columns match by name (case-insensitive). Common
        defaults are filled in for advanced fields; edit individual assets
        afterward to refine.
      </p>

      {/* Reference info */}
      <div className="mb-4 grid grid-cols-2 gap-4 text-xs">
        <div className="border border-ink-100 bg-white rounded-sm p-3">
          <div className="text-ink-500 mb-1 uppercase tracking-wider text-[10px]">Available types</div>
          <div className="text-ink-700 font-mono">
            {(typesQ.data ?? []).map((t) => t.name).join(', ')}
          </div>
        </div>
        <div className="border border-ink-100 bg-white rounded-sm p-3">
          <div className="text-ink-500 mb-1 uppercase tracking-wider text-[10px]">Available systems</div>
          <div className="text-ink-700 font-mono">
            {(systemsQ.data ?? []).map((s) => s.name).join(', ') || '(none — create a system first)'}
          </div>
        </div>
      </div>

      {/* Action row */}
      <div className="flex items-center gap-2 mb-2">
        <button onClick={addRow} className="btn-secondary text-xs py-1 px-3">+ Add row</button>
        <button onClick={clearAll} className="btn-secondary text-xs py-1 px-3">Clear all</button>
        <div className="flex-grow" />
        <span className="text-xs text-ink-500">
          {submittableRows.length} non-blank row{submittableRows.length === 1 ? '' : 's'}
        </span>
        <button
          onClick={() => submit.mutate()}
          disabled={!allValid || submit.isPending}
          className="btn-primary text-xs py-1 px-3"
        >
          {submit.isPending ? 'Creating…' : `Create ${submittableRows.length} asset${submittableRows.length === 1 ? '' : 's'}`}
        </button>
      </div>

      {submitError && (
        <div className="mb-3 p-3 bg-accent/5 border border-accent/30 text-accent text-xs rounded-sm">
          {submitError}
        </div>
      )}

      {/* Grid */}
      <div className="border border-ink-100 bg-white rounded-sm overflow-x-auto">
        <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 32 }} />
            <col style={{ width: 220 }} />
            <col style={{ width: 160 }} />
            <col style={{ width: 160 }} />
            <col style={{ width: 130 }} />
            <col style={{ width: 130 }} />
            <col style={{ width: 100 }} />
          </colgroup>
          <thead className="bg-ink-50/80 border-b border-ink-100">
            <tr>
              <th></th>
              <Th>Name</Th>
              <Th>Type</Th>
              <Th>System</Th>
              <Th>Criticality</Th>
              <Th>Exposure</Th>
              <Th>Vendor supplied</Th>
            </tr>
          </thead>
          <tbody onPaste={(e) => {
            // Only fire when the active element is a cell input we can reach.
            const target = e.target as HTMLElement;
            const td = target.closest('td');
            const tr = target.closest('tr');
            if (!td || !tr) return;
            const colIdx = Array.from(tr.children).indexOf(td) - 1;  // first col is delete btn
            const rowIdx = Array.from(tr.parentElement!.children).indexOf(tr);
            if (colIdx >= 0) onPaste(e, rowIdx, colIdx);
          }}>
            {rows.map((row, i) => {
              const v = validation[i]!;
              const errs = v.errors;
              return (
                <tr key={row.rowKey} className="border-b border-ink-100">
                  <td className="px-1 text-center">
                    <button
                      onClick={() => removeRow(i)}
                      className="text-ink-300 hover:text-accent text-xs"
                      aria-label={`Remove row ${i + 1}`}
                      tabIndex={-1}
                    >
                      ✕
                    </button>
                  </td>
                  <Cell error={errs.name}>
                    <input type="text" value={row.name} onChange={(e) => updateCell(i, 'name', e.target.value)}
                      placeholder="Asset name" className="w-full text-xs h-7 px-2 py-0 border-0 bg-transparent focus:bg-white" />
                  </Cell>
                  <Cell error={errs.type}>
                    <input type="text" value={row.type} onChange={(e) => updateCell(i, 'type', e.target.value)}
                      placeholder="e.g. Database" className="w-full text-xs h-7 px-2 py-0 border-0 bg-transparent focus:bg-white" />
                  </Cell>
                  <Cell error={errs.system}>
                    <input type="text" value={row.system} onChange={(e) => updateCell(i, 'system', e.target.value)}
                      placeholder="e.g. Core Banking" className="w-full text-xs h-7 px-2 py-0 border-0 bg-transparent focus:bg-white" />
                  </Cell>
                  <Cell error={errs.criticality}>
                    <select value={row.criticality} onChange={(e) => updateCell(i, 'criticality', e.target.value)}
                      className="w-full text-xs h-7 py-0 px-1 border-0 bg-transparent">
                      {LIKERT.map((l) => <option key={l} value={l}>{l.replace('_', ' ')}</option>)}
                    </select>
                  </Cell>
                  <Cell error={errs.exposure}>
                    <select value={row.exposure} onChange={(e) => updateCell(i, 'exposure', e.target.value)}
                      className="w-full text-xs h-7 py-0 px-1 border-0 bg-transparent">
                      {EXPOSURES.map((l) => <option key={l} value={l}>{l.replace('_', ' ')}</option>)}
                    </select>
                  </Cell>
                  <Cell error={errs.vendor}>
                    <select value={row.vendor} onChange={(e) => updateCell(i, 'vendor', e.target.value)}
                      className="w-full text-xs h-7 py-0 px-1 border-0 bg-transparent">
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </select>
                  </Cell>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Per-row error summary */}
      {validation.some((v) => !v.isBlank && Object.keys(v.errors).length > 0) && (
        <div className="mt-3 text-xs text-ink-500">
          <strong className="text-accent">Errors:</strong>
          <ul className="mt-1 space-y-0.5">
            {validation.flatMap((v, i) =>
              v.isBlank ? [] :
              Object.entries(v.errors).map(([field, msg]) => (
                <li key={`${i}-${field}`}>
                  Row {i + 1}, {field}: {msg}
                </li>
              )),
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="text-left text-[11px] font-medium text-ink-500 uppercase tracking-wider px-3 py-2">
      {children}
    </th>
  );
}

interface CellProps { children: React.ReactNode; error?: string | undefined }
function Cell({ children, error }: CellProps) {
  return (
    <td className={`p-0 ${error ? 'ring-1 ring-inset ring-accent/40 bg-accent/5' : ''}`}>
      {children}
    </td>
  );
}
